import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import { fetchMrrGoodsReceipts, type GoodsReceiptsListQuery, type MrrGoodsReceipt } from "../api/mrrInventoryApi";

const DEFAULT_LIMIT = 1500;

type AppliedFilters = {
  search: string;
  from: string;
  to: string;
  storeYardId: string;
  sort: "asc" | "desc";
  mrrId: string;
};

const emptyApplied: AppliedFilters = {
  search: "",
  from: "",
  to: "",
  storeYardId: "",
  sort: "desc",
  mrrId: "",
};

export function GoodsReceiptsPage() {
  const { storeYards } = useAppState();
  const sortedYards = useMemo(() => [...storeYards].sort((a, b) => a.sortOrder - b.sortOrder), [storeYards]);

  const [rows, setRows] = useState<MrrGoodsReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [draft, setDraft] = useState<AppliedFilters>(() => ({ ...emptyApplied }));
  const [applied, setApplied] = useState<AppliedFilters>(() => ({ ...emptyApplied }));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const q: GoodsReceiptsListQuery = {
      limit: DEFAULT_LIMIT,
      sort: applied.sort,
    };
    if (applied.search.trim()) q.search = applied.search.trim();
    if (applied.from.trim()) q.from = applied.from.trim();
    if (applied.to.trim()) q.to = applied.to.trim();
    if (applied.storeYardId.trim()) q.storeYardId = applied.storeYardId.trim();
    const mid = Number.parseInt(applied.mrrId.trim(), 10);
    if (Number.isFinite(mid) && mid > 0) q.mrrId = mid;

    try {
      const list = await fetchMrrGoodsReceipts(q);
      setRows(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = () => {
    setApplied({ ...draft });
    setExpandedId(null);
  };

  const clearFilters = () => {
    setDraft({ ...emptyApplied });
    setApplied({ ...emptyApplied });
    setExpandedId(null);
  };

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Inventory", to: "/inventory/inward" },
          { label: "Goods receipts" },
        ]}
        title="Goods receipts"
      />

      <div className="card card--gov card--elevated">
        <p className="section-lead section-lead--tight text-muted" style={{ marginTop: 0 }}>
          Records from <code>inventory_mrr_goods_receipt</code> on operational MySQL.{" "}
          <Link to="/inventory/inward">Record inward</Link>
          {" · "}
          <Link to="/inventory/store-stocks">Store stocks</Link>
        </p>

        <form
          className="goods-receipts-filters"
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters();
          }}
        >
          <div className="goods-receipts-filters__grid">
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Search</span>
              <input
                type="search"
                className="form-control"
                placeholder="Receipt #, MRR #, store, received by, notes…"
                value={draft.search}
                onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
                autoComplete="off"
              />
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">From date</span>
              <input
                type="date"
                className="form-control"
                value={draft.from}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
              />
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">To date</span>
              <input
                type="date"
                className="form-control"
                value={draft.to}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
              />
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Store / yard</span>
              <select
                className="form-control"
                value={draft.storeYardId}
                onChange={(e) => setDraft((d) => ({ ...d, storeYardId: e.target.value }))}
              >
                <option value="">All</option>
                {sortedYards.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">MRR id</span>
              <input
                type="number"
                min={1}
                className="form-control"
                placeholder="Optional"
                value={draft.mrrId}
                onChange={(e) => setDraft((d) => ({ ...d, mrrId: e.target.value }))}
              />
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Sort by time</span>
              <select
                className="form-control"
                value={draft.sort}
                onChange={(e) => setDraft((d) => ({ ...d, sort: e.target.value as "asc" | "desc" }))}
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>
            </label>
          </div>
          <div className="goods-receipts-filters__actions">
            <button type="submit" className="btn btn--primary btn--tg btn--sm" disabled={loading}>
              Apply filters
            </button>
            <button type="button" className="btn btn--tg-secondary btn--sm" onClick={clearFilters} disabled={loading}>
              Clear all
            </button>
          </div>
        </form>

        {!loading && !error ? (
          <p className="text-muted section-lead section-lead--tight" style={{ marginBottom: "0.65rem" }}>
            Showing <strong>{rows.length}</strong> receipt{rows.length === 1 ? "" : "s"}
            {rows.length >= DEFAULT_LIMIT ? ` (limit ${DEFAULT_LIMIT} — narrow filters for older rows)` : ""}
          </p>
        ) : null}

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : error ? (
          <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
            {error}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-muted">No goods receipts match these filters.</p>
        ) : (
          <div style={{ overflowX: "auto", marginTop: "0.75rem" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th title="Goods receipt number">Receipt #</th>
                  <th>MRR</th>
                  <th>Store / yard</th>
                  <th>Items</th>
                  <th>Received by</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((rec) => {
                  const open = expandedId === rec.id;
                  return (
                    <Fragment key={rec.id}>
                      <tr>
                        <td>{new Date(rec.createdAt).toLocaleString()}</td>
                        <td>
                          <strong>{rec.id}</strong>
                        </td>
                        <td>
                          <strong>{rec.mrrNumber}</strong>
                        </td>
                        <td>{rec.storeYardName || rec.storeYardId}</td>
                        <td>{rec.lineReceipts.length}</td>
                        <td>{rec.receivedBy || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--link btn--sm"
                            onClick={() => setExpandedId(open ? null : rec.id)}
                          >
                            {open ? "Hide items" : "View items"}
                          </button>
                        </td>
                      </tr>
                      {open ? (
                        <tr className="goods-receipt-detail-row">
                          <td colSpan={7} style={{ background: "#f8fafc", padding: "0.85rem 1rem" }}>
                            <table className="data-table" style={{ margin: 0 }}>
                              <thead>
                                <tr>
                                  <th>Item</th>
                                  <th>Unit</th>
                                  <th>Qty inward</th>
                                  <th>Reject qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rec.lineReceipts.map((ln, i) => (
                                  <tr key={`${rec.id}-ln-${i}`}>
                                    <td>{ln.itemName?.trim() ? ln.itemName : "—"}</td>
                                    <td>{ln.itemUnit?.trim() ? ln.itemUnit : "—"}</td>
                                    <td>{ln.qtyReceived}</td>
                                    <td>{ln.qtyRejected ?? 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {rec.notes ? (
                              <p className="text-muted section-lead section-lead--tight" style={{ marginTop: "0.65rem" }}>
                                <strong>Notes:</strong> {rec.notes}
                              </p>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
